import React, { useState, useEffect } from 'react';
import { Carousel } from '@mantine/carousel';
import { css, useTheme } from '@emotion/react';
import { useScreenLayout } from 'common-ui';

interface CmsCarouselProps {
  children: React.ReactNode;
  slidesPerBlock: number;
  slidesToScroll: number;
  dataTestId?: string;
  style?: Record<string, any>;
  initialSlide?: number;
  withIndicators?: boolean;
  loop?: boolean;
}

export const CmsCarousel = ({
  slidesPerBlock,
  slidesToScroll,
  children,
  dataTestId = 'cms-carousel',
  style,
  initialSlide = 0,
  withIndicators,
  loop = true,
}: CmsCarouselProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenLayout();
  const [slideSize, setSlideSize] = useState<string | null>(null);

  useEffect(() => {
    const computedSlideSize = `${100 / slidesPerBlock}%`;
    setSlideSize(computedSlideSize);
  }, [slidesPerBlock, slidesToScroll]);

  if (slideSize === null) {
    return null;
  }

  const isScrollable = Array.isArray(children) && children?.length > (isMobile ? 1 : slidesPerBlock);

  const styles = (style ? style : theme.widgets.CmsCarousel) || {};

  const containerCss = css`
    .mantine-Carousel-container {
      transform: ${isScrollable ? '' : 'translate3d(0px, 0px, 0px) !important'};
    }
  `;

  return (
    <div css={containerCss}>
      <Carousel
        withIndicators={withIndicators ?? isScrollable}
        withControls={isScrollable}
        loop={isScrollable && loop}
        slidesToScroll={isMobile ? 1 : slidesToScroll}
        slideSize={isMobile ? '100%' : slideSize}
        align="start"
        slideGap="md"
        controlsOffset="md"
        styles={styles}
        data-testid={dataTestId}
        initialSlide={initialSlide}
      >
        {children}
      </Carousel>
    </div>
  );
};

interface CmsCarouselSlideProps {
  children: React.ReactNode;
  dataTestId?: string;
}

export const CmsCarouselSlide = ({ children, dataTestId = 'cms-slide' }: CmsCarouselSlideProps) => (
  <Carousel.Slide data-testid={dataTestId}>{children}</Carousel.Slide>
);

CmsCarousel.CmsCarouselSlide = CmsCarouselSlide;
